import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./IncidentsList.module.css";
import IncidentItem from "Security/Shared/IncidentItem";
import TableSortItem from "shared/TableSortItem";
import PaginationControls from "shared/PaginationControls";
import classNames from "classnames";
/**
 * Interactive component.
 * Displays a list of incidents with a header.
 */
export function IncidentsList({ incidents, resultsPerPage }) {
  const [incidentSortOrder, setIncidentSortOrder] = useState("desc");
  const [statusSortOrder, setStatusSortOrder] = useState("desc");
  const [typeSortOrder, setTypeSortOrder] = useState("desc");
  const [severitySortOrder, setSeveritySortOrder] = useState("desc");
  const [iboSortOrder, setIboSortOrder] = useState("desc");
  const [ownerSortOrder, setOwnerSortOrder] = useState("desc");
  const [createdSortOrder, setCreatedSortOrder] = useState("desc");
  const [sortedIncidents, setSortedIncidents] = useState(incidents);
  const [paginatedIncidents, setPaginatedIncidents] = useState(incidents);
  const [currentPage, setCurrentPage] = useState(1);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US");
  };

  const lastPage = useMemo(
    () => (incidents ? Math.ceil(incidents.length / resultsPerPage) : 1),
    [incidents]
  );

  useEffect(() => {
    setSortedIncidents(incidents);
  }, [incidents]);

  const onPageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  /**
   * Sort list by property when the sort filter changes.
   */
  function sort(prop, sortOrder) {
    let sortedArray;
    if (sortOrder === "asc") {
      switch (prop) {
        case "Incident #":
          sortedArray = [...incidents].sort(
            (a, b) => a.id.replace(/\D/g, "") - b.id.replace(/\D/g, "")
          );

          break;
        case "Status":
          sortedArray = [...incidents].sort((a, b) => {
            if (!a.status || !b.status) {
              if (!a.status && !b.status) return 0;
              if (!a.status) return 1;
              if (!b.status) return -1;
            } else {
              return a.status.localeCompare(b.status);
            }
          });
          break;
        case "Type":
          sortedArray = [...incidents].sort((a, b) => {
            if (!a.problemType || !b.problemType) {
              if (!a.problemType && !b.problemType) return 0;
              if (!a.problemType) return 1;
              if (!b.problemType) return -1;
            } else {
              return a.problemType.localeCompare(b.problemType);
            }
          });
          break;
        case "Severity":
          sortedArray = [...incidents].sort((a, b) => {
            if (!a.severity || !b.severity) {
              if (!a.severity && !b.severity) return 0;
              if (!a.severity) return 1;
              if (!b.severity) return -1;
            } else {
              return (
                a.severity.replace(/\D/g, "") - b.severity.replace(/\D/g, "")
              );
            }
          });

          break;
        case "IBO #":
          sortedArray = [...incidents].sort((a, b) => {
            if (
              !a.complaintAgainst.iboNumber ||
              !b.complaintAgainst.iboNumber
            ) {
              if (
                !a.complaintAgainst.iboNumber &&
                !b.complaintAgainst.iboNumber
              )
                return 0;
              if (!a.complaintAgainst.iboNumber) return 1;
              if (!b.complaintAgainst.iboNumber) return -1;
            } else {
              return a.complaintAgainst.iboNumber
                .toString()
                .localeCompare(b.complaintAgainst.iboNumber.toString());
            }
          });

          break;
        case "Case Owner":
          sortedArray = [...incidents].sort((a, b) => {
            if (!a.caseOwner || !b.caseOwner) {
              if (!a.caseOwner && !b.caseOwner) return 0;
              if (!a.caseOwner) return 1;
              if (!b.caseOwner) return -1;
            } else {
              return a.caseOwner.localeCompare(b.caseOwner);
            }
          });

          break;
        case "Date Created":
          sortedArray = [...incidents].sort(
            (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)
          );

          break;

        default:
          sortedArray = incidents;
      }
    } else {
      switch (prop) {
        case "Incident #":
          sortedArray = [...incidents].sort(
            (a, b) => b.id.replace(/\D/g, "") - a.id.replace(/\D/g, "")
          );

          break;
        case "Status":
          sortedArray = [...incidents].sort((a, b) => {
            if (!a.status || !b.status) {
              if (!a.status && !b.status) return 0;
              if (!a.status) return 1;
              if (!b.status) return -1;
            } else {
              return b.status.localeCompare(a.status);
            }
          });

          break;
        case "Type":
          sortedArray = [...incidents].sort((a, b) => {
            if (!a.problemType || !b.problemType) {
              if (!a.problemType && !b.problemType) return 0;
              if (!a.problemType) return 1;
              if (!b.problemType) return -1;
            } else {
              return b.problemType.localeCompare(a.problemType);
            }
          });

          break;
        case "Severity":
          sortedArray = [...incidents].sort((a, b) => {
            if (!a.severity || !b.severity) {
              if (!a.severity && !b.severity) return 0;
              if (!a.severity) return 1;
              if (!b.severity) return -1;
            } else {
              return (
                b.severity.replace(/\D/g, "") - a.severity.replace(/\D/g, "")
              );
            }
          });

          break;
        case "IBO #":
          sortedArray = [...incidents].sort((a, b) => {
            if (
              !a.complaintAgainst.iboNumber ||
              !b.complaintAgainst.iboNumber
            ) {
              if (
                !a.complaintAgainst.iboNumber &&
                !b.complaintAgainst.iboNumber
              )
                return 0;
              if (!a.complaintAgainst.iboNumber) return 1;
              if (!b.complaintAgainst.iboNumber) return -1;
            } else {
              return b.complaintAgainst.iboNumber
                .toString()
                .localeCompare(a.complaintAgainst.iboNumber.toString());
            }
          });

          break;
        case "Case Owner":
          sortedArray = [...incidents].sort((a, b) => {
            if (!a.caseOwner || !b.caseOwner) {
              if (!a.caseOwner && !b.caseOwner) return 0;
              if (!a.caseOwner) return 1;
              if (!b.caseOwner) return -1;
            } else {
              return b.caseOwner.localeCompare(a.caseOwner);
            }
          });

          break;
        case "Date Created":
          sortedArray = [...incidents].sort(
            (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
          );

          break;
        default:
          sortedArray = incidents;
      }
    }

    setSortedIncidents(sortedArray);
  }

  useEffect(() => {
    const firstPageIndex = (currentPage - 1) * resultsPerPage;
    const lastPageIndex = firstPageIndex + resultsPerPage;
    if (sortedIncidents.length)
      setPaginatedIncidents(
        sortedIncidents.slice(firstPageIndex, lastPageIndex)
      );
  }, [
    currentPage,
    sortedIncidents,
    incidentSortOrder,
    statusSortOrder,
    typeSortOrder,
    severitySortOrder,
    iboSortOrder,
    ownerSortOrder,
    createdSortOrder,
  ]);

  return (
    <div className={styles.containerVenue}>
      <div className={styles.headerContainer}>
        <div className={styles.sortItem}>
          <TableSortItem
            value={incidentSortOrder}
            title="Incident #"
            onChange={() => {
              setIncidentSortOrder(
                incidentSortOrder === "asc" ? "desc" : "asc"
              );
              sort("Incident #", incidentSortOrder);
            }}
          />
        </div>
        <div className={styles.sortItem}>
          <TableSortItem
            value={statusSortOrder}
            title="Status"
            onChange={() => {
              setStatusSortOrder(statusSortOrder === "asc" ? "desc" : "asc");
              sort("Status", statusSortOrder);
            }}
          />
        </div>
        <div className={styles.sortItem}>
          <TableSortItem
            value={typeSortOrder}
            title="Type"
            onChange={() => {
              setTypeSortOrder(typeSortOrder === "asc" ? "desc" : "asc");
              sort("Type", typeSortOrder);
            }}
          />
        </div>
        <div className={styles.sortItem}>
          <TableSortItem
            value={severitySortOrder}
            title="Severity"
            onChange={() => {
              setSeveritySortOrder(
                severitySortOrder === "asc" ? "desc" : "asc"
              );
              sort("Severity", severitySortOrder);
            }}
          />
        </div>
        <div className={styles.sortItem}>
          <TableSortItem
            value={iboSortOrder}
            title="IBO #"
            onChange={() => {
              setIboSortOrder(iboSortOrder === "asc" ? "desc" : "asc");
              sort("IBO #", iboSortOrder);
            }}
          />
        </div>
        <div className={styles.sortItem}>
          <TableSortItem
            value={ownerSortOrder}
            title="Case Owner"
            onChange={() => {
              setOwnerSortOrder(ownerSortOrder === "asc" ? "desc" : "asc");
              sort("Case Owner", ownerSortOrder);
            }}
          />
        </div>
        <div className={styles.sortItem}>
          <TableSortItem
            value={createdSortOrder}
            title="Date Created"
            onChange={() => {
              setCreatedSortOrder(createdSortOrder === "asc" ? "desc" : "asc");
              sort("Date Created", createdSortOrder);
            }}
          />
        </div>
      </div>
      {incidents &&
        incidents.length > 0 &&
        paginatedIncidents.map((item, index) => (
          <div
            className={`${index % 2 !== 0 ? styles.otherBgColor : ""} ${
              index === paginatedIncidents.length - 1 ? styles.lastItem : ""
            }`}
            key={index}
          >
            <IncidentItem
              index={index}
              IncidentDetails={{
                ...item,
                createdAt: formatDate(item.createdAt),
              }}
              onClick={() => {}}
              onIboClick={() => alert("TODO: onIboClick called")}
            />
          </div>
        ))}

      {incidents.length <= resultsPerPage ? null : (
        <div className={styles.paginationWrapper}>
          <div className={styles.paginationControls}>
            <PaginationControls
              page={currentPage}
              lastPage={lastPage}
              onPageChange={onPageChange}
              disableNext={currentPage === lastPage}
              disabled={!incidents}
              color={"#1F3BD8"}
            />
          </div>
        </div>
      )}
    </div>
  );
}

IncidentsList.propTypes = {
  /**
   * Array of objects with the incident details.
   */
  incidents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      status: PropTypes.string,
      problemType: PropTypes.string,
      severity: PropTypes.string,
      iboNum: PropTypes.string,
      caseOwner: PropTypes.string,
      createdAt: PropTypes.string,
    })
  ),
};
